import { Link, useParams } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { PageHero, Section, Seo, Reveal } from "@/components/site/Section";
import { FinalCTA } from "@/components/site/HomeSections";
import SEO from "@/data/seo-pages.json";
import NotFound from "@/pages/NotFound";

const SITE = process.env.REACT_APP_CANONICAL_URL;

const guideLd = (g, path) => [
  { "@context": "https://schema.org", "@type": "BreadcrumbList", itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
    { "@type": "ListItem", position: 2, name: "Guides", item: `${SITE}/guides` },
    { "@type": "ListItem", position: 3, name: g.h1, item: `${SITE}${path}` }] },
  { "@context": "https://schema.org", "@type": "Article", headline: g.title, description: g.description, mainEntityOfPage: `${SITE}${path}`,
    datePublished: SEO.updated, dateModified: SEO.updated, image: `${SITE}/img/og-image.jpg`,
    author: { "@type": "Organization", name: "WeeLogRun", url: `${SITE}/` }, publisher: { "@id": `${SITE}/#business` } },
];

const MoreGuides = ({ current }) => (
  <ul className="grid gap-3 sm:grid-cols-2" data-testid="more-guides">
    {SEO.guides.filter((g) => g.slug !== current).map((g) => (
      <li key={g.slug}>
        <Link to={`/guides/${g.slug}`} className="lc-card flex items-center justify-between gap-3 p-5 text-[#F7F5F0] transition-colors hover:text-[#FF6A00]" data-testid={`guide-link-${g.slug}`}>
          {g.h1}<ArrowRight className="h-4 w-4 shrink-0" />
        </Link>
      </li>
    ))}
  </ul>
);

export default function Guide() {
  const { slug } = useParams();
  const g = SEO.guides.find((x) => x.slug === slug);
  if (!g) return <NotFound />;
  const path = `/guides/${g.slug}`;
  return (
    <>
      <Seo title={g.title} description={g.description} path={path} jsonLd={guideLd(g, path)} />
      <PageHero eyebrow="Firewood guide" title={g.h1} intro={g.intro} />
      <Section>
        <article className="max-w-3xl space-y-10" data-testid="guide-article">
          {g.sections.map((s) => (
            <Reveal key={s.h}>
              <h2 className="font-display text-2xl font-bold text-[#F7F5F0] sm:text-3xl">{s.h}</h2>
              {s.p.map((p) => <p key={p.slice(0, 24)} className="mt-4 text-base leading-relaxed text-[#A1A1AA] md:text-lg">{p}</p>)}
            </Reveal>
          ))}
          <div className="flex flex-wrap gap-3 pt-4">
            <Link to="/plans" className="lc-btn-primary" data-testid="guide-cta-plans">See our plans</Link>
            <Link to="/delivery-areas#check" className="lc-btn-ghost" data-testid="guide-cta-postcode">Check your postcode</Link>
          </div>
        </article>
        <div className="mt-16">
          <p className="mb-4 text-sm uppercase tracking-[0.2em] text-[#D4A437]">More guides</p>
          <MoreGuides current={g.slug} />
        </div>
      </Section>
      <FinalCTA />
    </>
  );
}
